<template>
  <NuxtLayout
    v-if="data"
    :name="data.customisable ? 'default' : 'unstyled'"
    hide-footer
  >
    <template #header-content>
      <div
        v-if="data.customisable"
        class="grid grid-cols-2 gap-x-6 border-b px-6 py-5 text-sm lg:grid-cols-[max-content_1fr_max-content] lg:border-b lg:bg-white lg:py-4"
      >
        <NuxtLink
          :to="`/${data.partner.slug}/experiences`"
          class="flex items-center space-x-3 text-supplement"
        >
          <Icon icon="chevron-left" />
          <span><span class="hidden md:inline-block">{{ data?.partner.name }}&nbsp;</span>{{ $t('experiences') }}</span>
        </NuxtLink>

        <div class="flex items-center justify-end space-x-6 lg:justify-center">
          <PersonalisationsDropdown
            :shared-collection-hashid="data.hashid"
            :personalisations="data.personalisations"
          />

          <div class="hidden lg:flex lg:items-center lg:space-x-3">
            <ToggleSwitch
              v-model="minimalUI"
              size="sm"
            />
            <div class="space-x-2.5">
              <span class="font-semibold">{{ $t('preview') }}</span>
              <span
                class="text-supplement opacity-0 transition-opacity"
                :class="{ 'opacity-100': minimalUI }"
              >&mdash;</span>
              <span
                v-if="minimalUI"
                class="text-gray-500 opacity-0 transition-opacity"
                :class="{ 'opacity-100': minimalUI }"
              >{{ $t('preview_description') }}</span>
            </div>
          </div>
        </div>

        <div class="group hidden lg:flex lg:items-center lg:justify-end lg:space-x-6">
          <div>
            <span
              v-if="isExpired"
              class="rounded bg-gray-400 px-2 py-1 text-xs font-semibold text-gray-100 transition-colors group-hover:bg-red-600 group-hover:text-red-100"
            >{{ $t('expired') }}</span>
          </div>
          <ShareButtons
            :collection-name="data.collection.name"
            :social-share-text="data?.collection.social_share_text"
            :landing-page-url="data.landing_page_url"
            :embed-code="data.embed_code"
            :partner-name="data.partner.name"
            :media="data.media"
            :class="{ 'opacity-50 transition-opacity group-hover:opacity-100': isExpired }"
          />
        </div>
      </div>

      <div class="border-b p-2 lg:hidden">
        <nav class="grid grid-cols-3 rounded-md bg-slate-100 p-1.5 text-xs font-semibold">
          <a @click="activeMobileTab = 'edit'">
            <span
              class="flex items-center justify-center rounded-md px-3 py-2.5 transition-colors"
              :class="[
                activeMobileTab === 'edit'
                  ? 'cursor-default bg-primary text-white shadow'
                  : 'text-slate-500 hover:text-primary'
              ]"
            >{{ $t('personalize') }}</span>
          </a>
          <a @click="activeMobileTab = 'view'">
            <span
              class="flex items-center justify-center rounded-md px-3 py-2.5 transition-colors"
              :class="[
                activeMobileTab === 'view'
                  ? 'cursor-default bg-primary text-white shadow'
                  : 'text-slate-500 hover:text-primary'
              ]"
            >{{ $t('view') }}</span>
          </a>
          <a @click="activeMobileTab = 'share'">
            <span
              class="flex items-center justify-center rounded-md px-3 py-2.5 transition-colors"
              :class="[
                activeMobileTab === 'share'
                  ? 'cursor-default bg-primary text-white shadow'
                  : 'text-slate-500 hover:text-primary'
              ]"
            >{{ $t('share') }}</span>
          </a>
        </nav>
      </div>
    </template>

    <div class="relative flex grow flex-col overflow-hidden">
      <div class="flex grow overflow-y-auto overflow-x-hidden lg:overflow-hidden">
        <div
          v-if="data.customisable"
          class="w-full shrink-0 flex-col transition-all lg:flex lg:w-80 lg:border-r"
          :class="[minimalUI ? 'lg:-ml-80' : 'lg:ml-0', activeMobileTab === 'edit' ? 'flex' : 'hidden']"
        >
          <LazyCollectionLiveEditor
            v-model:first-name="data.contact.first_name"
            v-model:last-name="data.contact.last_name"
            v-model:phone="data.contact.phone"
            v-model:logo="data.advisor.logo"
            v-model:personal-photo="data.advisor.personal_photo"
            v-model:personal-message="currentPersonalisation.personal_message"
            v-model:cta-display="data.cta.display"
            v-model:cta-link="data.cta.values.custom.link"
            v-model:cta-label="data.cta.values.custom.label"
            :shared-collection-hashid="data.hashid"
            :cta-enabled="data.cta.enabled"
            :cta-options="data.cta.options"
          />
        </div>

        <div
          class="grow flex-col overflow-y-auto transition-spacing lg:flex"
          :class="[
            {
              'bg-slate-50': data.customisable,
              'lg:p-12': data.customisable && !minimalUI
            },
            activeMobileTab === 'view' ? 'flex' : 'hidden'
          ]"
        >
          <div class="flex grow flex-col space-y-6">
            <LandingPage
              mode="shared-landing-page"
              :data="data"
              :current-personalisation="currentPersonalisation"
              :class="{ 'bg-white shadow-lg': data.customisable }"
            />
          </div>
        </div>

        <div
          v-if="data.customisable"
          class="grow flex-col overflow-y-auto p-8 transition-spacing md:p-10 lg:hidden"
          :class="[activeMobileTab === 'share' ? 'flex' : 'hidden']"
        >
          <ShareButtons
            :collection-name="data.collection.name"
            :social-share-text="data?.collection.social_share_text"
            :embed-code="data.embed_code"
            :landing-page-url="data.landing_page_url"
            :partner-name="data.partner.name"
            :media="data.media"
          />
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { captureCollectionView, channel } from '@syntax51/ag-analytics'
import type { LaravelResponse } from '~/types/api'
import type { LandingPage } from '~/types/landing-page'

const route = useRoute()
const { fbAppId, siteName } = useRuntimeConfig().public
const user = useUser()
const { isLoggedIn } = useAuth()

// Fetch data
const personalisationHashid = getVersionHashidFromUrl()

const { data: _data } = await useAsyncData<LaravelResponse<LandingPage>>(`landing-page-${personalisationHashid}`, () =>
  $larafetch(`/collections/${personalisationHashid}`)
)

if (!_data.value) {
  throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true })
}

const { data, getDefaultPersonalisation, getPersonalisation } = useLandingPage()
data.value = _data.value.data

// Handle redirect if required
if (route.params.slug !== data.value.collection.slug) {
  // console.log("slug mismatch", route.params.slug, data.value.collection.slug);
  // await navigateTo(`/experiences/${data.value.collection.slug}-${personalisationHashid}`, {
  //   replace: true,
  //   redirectCode: 301,
  // });
}

// Track experience view
if (import.meta.client && !isLoggedIn.value) {
  captureCollectionView({
    channel: channel.INSPIRES,
    partnerId: data.value?.partner.id,
    userId: data.value?.advisor.id,
    collectionId: data.value?.collection.hash_id,
    sharedCollectionId: data.value?.hashid,
    personalisationId: personalisationHashid,
    contentIds: data.value?.collection.blocks.map(block => block.hashid),
    viaPartnerId: undefined,
    collaboratorPartnerId: undefined
  })
}

const activeMobileTab = ref<'edit' | 'view' | 'share'>(
  data.value?.customisable && !user.value?.first_name ? 'edit' : 'view'
)

const currentPersonalisation = ref(getPersonalisation(personalisationHashid))

// Redirect to default version if authorised user tries to access a collaborator version
if (data.value?.customisable && currentPersonalisation.value.collaborator_version) {
  let defaultPersonalisation = getDefaultPersonalisation()

  if (!defaultPersonalisation) {
    const { data } = await useAsyncData(`landing-page-${personalisationHashid}-default-version`, () =>
      $larafetch(`extended-collections/${data.value.hashid}/personalisations/default`)
    )

    defaultPersonalisation = data
  }

  await navigateTo(`/experiences/${data.value.collection.slug}-${defaultPersonalisation.hashid}`, {
    replace: true,
    redirectCode: 301
  })
}

// In the case of collaborator versions where the version is not returned, set the current version to the default version
if (!currentPersonalisation.value) {
  currentPersonalisation.value = getDefaultPersonalisation()
}

// Guest preview
const { minimalUI } = useLayoutState()

const isExpired = data.value.collection.expires_at && new Date(data.value.collection.expires_at) < new Date()

// Head
useHead({
  title: data.value.collection.name_clean,
  titleTemplate: '%s',
  meta: [
    // OpenGraph
    { property: 'fb:app_id', content: fbAppId },
    { property: 'og:type', content: 'article' },
    { property: 'og:url', content: data.value.seo.canonical_url },
    { property: 'og:title', content: data.value.seo.title },
    { property: 'og:description', content: data.value.seo.description },
    { property: 'og:image', content: data.value.seo.image_url },
    { property: 'og:locale', content: 'en' },
    { property: 'og:site_name', content: siteName },
    // Twitter Card
    { name: 'twitter:card', content: 'summary' },
    { name: 'twitter:title', content: data.value.seo.title },
    { name: 'twitter:description', content: data.value.seo.description },
    { name: 'twitter:image', content: data.value.seo.image_url }
  ],
  script: [{ src: '//w.soundcloud.com/player/api.js' }]
})

// Google Fonts
const usesGoogleFonts = computed(
  () =>
    data.value
    && (data.value.collection.customisations.body_font_type === 'google-font'
      || data.value.collection.customisations.collection_title_font_type === 'google-font'
      || data.value.collection.customisations.content_title_font_type === 'google-font')
)

if (usesGoogleFonts.value) {
  useHead({
    link: [
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
        crossorigin: true
      },
      {
        rel: 'stylesheet',
        href: `https://fonts.googleapis.com/css2?family=${data.value.collection.customisations.google_fonts?.uri}&display=swap`
      }
    ]
  })
}

definePageMeta({
  hideNav: true,
  layout: false
})
</script>
